import React, { useState, useEffect } from 'react';
import { ShieldCheck } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  content: string;
  onConfirm: () => void;
}

function parseInline(text: string): React.ReactNode[] {
  const pattern = /(\*\*(.+?)\*\*|!!(.+?)!!|==(.+?)==)/g;
  const result: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  let idx = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) result.push(text.slice(last, match.index));
    if (match[2] !== undefined) {
      result.push(<strong key={idx++} className="font-bold text-primary-700">{match[2]}</strong>);
    } else if (match[3] !== undefined) {
      result.push(<strong key={idx++} className="font-bold text-gray-600">{match[3]}</strong>);
    } else if (match[4] !== undefined) {
      result.push(<mark key={idx++} className="bg-gray-100 text-gray-900 px-0.5 rounded font-medium not-italic">{match[4]}</mark>);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) result.push(text.slice(last));
  return result;
}

function renderContent(text: string) {
  return text.split('\n').map((line, i) => {
    const colonIdx = line.search(/[：:]/);
    if (colonIdx > 0 && colonIdx < 12) {
      const title = line.slice(0, colonIdx + 1);
      const body = line.slice(colonIdx + 1);
      return (
        <p key={i} className="mb-3 leading-relaxed text-gray-700 text-sm">
          <strong className="font-bold text-primary-700">{title}</strong>
          {parseInline(body)}
        </p>
      );
    }
    if (line.trim() === '') return <div key={i} className="mb-1" />;
    return <p key={i} className="mb-3 leading-relaxed text-gray-700 text-sm">{parseInline(line)}</p>;
  });
}

export default function InterviewCodeOfConductModal({ content, onConfirm }: Props) {
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[88vh]">

        {/* Header */}
        <div className="shrink-0 rounded-t-2xl bg-gradient-to-r from-primary-600 to-primary-500 px-6 pt-6 pb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <ShieldCheck size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white tracking-wide">留形面试官公约</h2>
              <p className="text-xs text-white/70 mt-0.5">请仔细阅读，知悉后方可进行面试</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {renderContent(content)}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex flex-col items-center gap-3">
          <p className="text-xs text-gray-400 text-center">
            {countdown > 0
              ? `请认真阅读以上公约，${countdown} 秒后可确认`
              : '我已认真阅读并承诺遵守以上公约'}
          </p>
          <button
            onClick={onConfirm}
            disabled={countdown > 0}
            className={clsx(
              'flex items-center justify-center gap-2 w-full max-w-xs py-2.5 rounded-xl text-sm font-semibold transition-all',
              countdown > 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700 text-white shadow-md shadow-primary-200'
            )}
          >
            {countdown > 0 ? (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-300 text-gray-500 text-xs font-bold">{countdown}</span>
            ) : (
              <ShieldCheck size={15} />
            )}
            {countdown > 0 ? `已知悉（${countdown}s）` : '已知悉，开始面试'}
          </button>
        </div>

      </div>
    </div>
  );
}
